/**
 * The card game a catalog item belongs to. `category` is the kind of thing (`TCG`, or `SPORTS`
 * for a sports card); the game is the finer split, and the Pokemon in-box floor applies only
 * to a raw card single of that game. The boxed import stores the same code on `subcategory`.
 */

import { getLeafSiteCategoryFromTags } from "./exchange-eligibility";

/** Display names for boxed's site-category codes; anything else is title-cased. */
const GAME_LABELS: Record<string, string> = {
  POKEMON: "Pokemon",
  MTG: "MTG",
  ONE_PIECE: "One Piece",
  LORCANA: "Lorcana",
  RIFTBOUND: "Riftbound",
  YUGIOH: "Yu-Gi-Oh!",
  DRAGON_BALL: "Dragon Ball",
  STAR_WARS: "Star Wars",
  DIGIMON: "Digimon",
  GUNDAM: "Gundam",
  SPORTS: "Sports",
  OTHER: "Other",
};

function gameFromAttributes(attributes: unknown): string | null {
  if (!attributes || typeof attributes !== "object") return null;
  const details = (attributes as Record<string, unknown>).details;
  if (!details || typeof details !== "object") return null;
  const value = (details as Record<string, unknown>).category;
  if (typeof value !== "string" || !value.trim()) return null;
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

/**
 * The item's game as a boxed site-category code (`POKEMON`, `MTG`, `ONE_PIECE`, …): the most
 * specific site-category tag, else the boxed asset group's `details.category` carried on
 * `attributes`. Null when the item names neither.
 */
export function gameForItem(item: { tags: string[] | null; attributes: unknown }): string | null {
  return getLeafSiteCategoryFromTags(item.tags ?? undefined) ?? gameFromAttributes(item.attributes);
}

/** `POKEMON` → "Pokemon", `ONE_PIECE` → "One Piece"; an unknown code is title-cased. */
export function gameLabel(game: string | null): string | null {
  if (!game) return null;
  return (
    GAME_LABELS[game] ??
    game
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}
